import { Observable } from 'rxjs';
import { TableRowAmountsLoader } from '../../types/manage-ceg-table-row-data.types';
import { inject, Injectable } from '@angular/core';
import { BudgetSegmentService } from '@shared/services/backend/budget-segment.service';
import {
  BudgetTimeframeBrief,
  ManageCegTableCellAllocationAmount,
  ManageCegTableCellAllocations,
  ManageCegTableRow,
  ManageCegTableRowAllocations,
} from '@manage-ceg/types/manage-ceg-page.types';
import { ObjectAmountsByTimeframes } from '@shared/types/object-amounts.interface';
import { BudgetSegmentAccess } from '@shared/types/segment.interface';
import { tap } from 'rxjs/operators';
import {
  addSegmentSpecificRowAllocationsData, calculatePresentationSegmentSum,
  getRowAllocationsFromAmountsData
} from '@manage-ceg/services/manage-ceg-table-row-data/amounts-loader.helpers';
import { createDeepCopy } from '@shared/utils/common.utils';
import { BudgetTimeframesType } from '@shared/types/budget.interface';
import { ManageTableRowType } from '@shared/enums/manage-table-row-type.enum';
import { FilterManagementService } from '../../../header-navigation/components/filters/filter-services/filter-management.service';

@Injectable()
export class SegmentGroupAmountsLoader implements TableRowAmountsLoader {
  private readonly segmentService = inject(BudgetSegmentService);
  private readonly filterManagementService = inject(FilterManagementService);

  public readonly rowType = ManageTableRowType.SegmentGroup;
  private segments: BudgetSegmentAccess[] = [];

  private static addSegmentAllocsToSegmentGroupAllocs(
    resultRowAllocations: ManageCegTableRowAllocations,
    segmentRowAllocations: ManageCegTableRowAllocations
  ): ManageCegTableRowAllocations {
    const resultTimeframeEntries =
      Object.entries(segmentRowAllocations).map(
        ([tfId, tableCellAllocations]) =>
          [tfId, SegmentGroupAmountsLoader.addTableCellAllocations(resultRowAllocations?.[tfId], tableCellAllocations)]
      );
    return Object.fromEntries(resultTimeframeEntries);
  }

  private static addTableCellAllocations(
    targetCellAllocations: ManageCegTableCellAllocations,
    incrementCellAllocations: ManageCegTableCellAllocations
  ): ManageCegTableCellAllocations  {
    if (!targetCellAllocations) {
      return createDeepCopy(incrementCellAllocations);
    }

    const resCellAllocationEntries = Object.entries(incrementCellAllocations).map(
      ([colName, allocAmounts]) =>
        [colName, SegmentGroupAmountsLoader.addCellColumnAmounts(targetCellAllocations[colName], allocAmounts)]
    );
    return Object.fromEntries(resCellAllocationEntries);
  }

  private static addCellColumnAmounts(
    targetCellColumnAmount: ManageCegTableCellAllocationAmount,
    incrementCellColumnAmount: ManageCegTableCellAllocationAmount
  ): ManageCegTableCellAllocationAmount {
    if (!targetCellColumnAmount) {
      return createDeepCopy(incrementCellColumnAmount);
    }

    const resAmount: ManageCegTableCellAllocationAmount = {
      ownAmount: targetCellColumnAmount.ownAmount + incrementCellColumnAmount.ownAmount
    };

    if (targetCellColumnAmount.campaignsAndPrograms != null || incrementCellColumnAmount.campaignsAndPrograms != null) {
      resAmount.campaignsAndPrograms =
        (targetCellColumnAmount.campaignsAndPrograms || 0) + (incrementCellColumnAmount.campaignsAndPrograms || 0);
    }

    if (targetCellColumnAmount.unallocated != null || incrementCellColumnAmount.unallocated) {
      resAmount.unallocated = (targetCellColumnAmount.unallocated || 0) + (incrementCellColumnAmount.unallocated || 0);
    }

    return resAmount;
  }

  public setSegments(segments: BudgetSegmentAccess[]): SegmentGroupAmountsLoader {
    this.segments = segments;
    return this;
  }


  private getGroupedSegments(groupIds: number[]): Record<number, number[]> {
    return this.segments.reduce(
      (groups, segment) => {
        if (segment.segment_group && groupIds.includes(segment.segment_group)) {
          groups[segment.segment_group] = [...(groups[segment.segment_group] || []), segment.id];
        }
        return groups;
      },
      {}
    );
  }

  public fillRowAmounts(
    budgetId: number,
    rows: ManageCegTableRow[],
    params: object,
    timeframesAll: Record<BudgetTimeframesType, BudgetTimeframeBrief[]>
  ): Observable<any> {
    
    const groupedSegments = this.getGroupedSegments(rows.map(row => row.objectId));
    // This will get all segments under the segment groups which the user has access to
    const segmentIds =
      Object.values(groupedSegments).reduce(
        (allSegmentIds, groupSegmentIds) => [...allSegmentIds, ...groupSegmentIds],
        []
      );
    
    let segments = (this.filterManagementService.currentFilterSetValue?.segments || []) as number[];  
    
    // If no segments are selected in filter, we load all segments under the segment groups which the user has access to
    if(segments.length === 0){
      segments = segmentIds;
    }  

    return this.segmentService.getAmountsByTimeframes(budgetId, segments, params).pipe(
      tap(segmentAmounts => {
        // const { segments: segmentAmountsData, objects: objectsAmountData } = segmentAmounts;
        const {  segment_groups, segmentgroups_objects } = segmentAmounts;
        rows.forEach(row => this.fillSegmentGroupRowAllocations(row, segment_groups, segmentgroups_objects, timeframesAll));
      })
    );
  }


  private fillSegmentGroupRowAllocations(
    row: ManageCegTableRow,
    segmentAmountsData: ObjectAmountsByTimeframes,
    objectsAmountData: ObjectAmountsByTimeframes,
    timeframesAll: Record<BudgetTimeframesType, BudgetTimeframeBrief[]>
  ): void {
    const groupId = row.objectId;
    const segmentAmountsForGroup =  segmentAmountsData[groupId]
    const objectsAmountsForGroup = objectsAmountData[groupId];
   
    const segmentRowAllocations = getRowAllocationsFromAmountsData(segmentAmountsForGroup);
    addSegmentSpecificRowAllocationsData(segmentRowAllocations, objectsAmountsForGroup);
    row.allocations =  SegmentGroupAmountsLoader.addSegmentAllocsToSegmentGroupAllocs(null, segmentRowAllocations)

    row.presentationAllocations = calculatePresentationSegmentSum<ManageCegTableCellAllocations>(row.allocations, timeframesAll);

  }
}
